import React from 'react';

// Base skeleton with pulse animation
const SkeletonBase = ({ className, children }) => (
  <div className={`animate-pulse bg-white/5 rounded ${className}`}>
    {children}
  </div>
);

// Card skeleton
export const CardSkeleton = () => (
  <SkeletonBase className="w-full h-48" />
);

// Text skeleton variants
export const TextSkeleton = ({ className = 'h-4 w-full' }) => (
  <SkeletonBase className={className} />
);

export const TitleSkeleton = () => (
  <SkeletonBase className="h-8 w-3/4 mb-4" />
);

export const ParagraphSkeleton = ({ lines = 3 }) => (
  <div className="space-y-2">
    {[...Array(lines)].map((_, i) => (
      <TextSkeleton key={i} className={i === lines - 1 ? 'h-4 w-2/3' : 'h-4 w-full'} />
    ))}
  </div>
);

// Gallery/Testimonial Card Skeleton
export const ImageCardSkeleton = () => (
  <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl overflow-hidden">
    <SkeletonBase className="w-full aspect-square" />
    <div className="p-4 space-y-2">
      <SkeletonBase className="h-5 w-3/4" />
      <SkeletonBase className="h-4 w-1/2" />
    </div>
  </div>
);

// Table/List Skeleton
export const TableRowSkeleton = ({ columns = 4 }) => (
  <div className="flex gap-4 p-4 border border-[#D4AF7A]/20 rounded-xl">
    {[...Array(columns)].map((_, i) => (
      <SkeletonBase key={i} className="h-4 flex-1" />
    ))}
  </div>
);

// Gallery Grid Skeleton
export const GalleryGridSkeleton = ({ count = 8 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
    {[...Array(count)].map((_, i) => (
      <ImageCardSkeleton key={i} />
    ))}
  </div>
);

// Testimonial List Skeleton
export const TestimonialListSkeleton = ({ count = 5 }) => (
  <div className="space-y-3">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <SkeletonBase className="w-12 h-12 rounded-full flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <SkeletonBase className="h-5 w-1/3" />
            <SkeletonBase className="h-4 w-full" />
            <SkeletonBase className="h-4 w-4/5" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// FAQ Item Skeleton
export const FAQItemSkeleton = ({ count = 3 }) => (
  <div className="space-y-3">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl overflow-hidden">
        <div className="p-6 flex items-center justify-between">
          <div className="flex-1 space-y-2">
            <SkeletonBase className="h-5 w-3/4" />
            <SkeletonBase className="h-4 w-1/4" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Package Card Skeleton
export const PackageCardSkeleton = ({ count = 3 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="bg-white/5 border border-[#D4AF7A]/20 rounded-2xl overflow-hidden">
        <div className="p-6 space-y-4">
          <SkeletonBase className="h-7 w-2/3" />
          <SkeletonBase className="h-10 w-1/2" />
          <div className="space-y-2 pt-4">
            {[...Array(4)].map((_, j) => (
              <SkeletonBase key={j} className="h-4 w-full" />
            ))}
          </div>
          <SkeletonBase className="h-12 w-full" />
        </div>
      </div>
    ))}
  </div>
);

// Stats Card Skeleton
export const StatsCardSkeleton = ({ count = 3 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <SkeletonBase className="h-4 w-24" />
            <SkeletonBase className="h-8 w-16" />
          </div>
          <SkeletonBase className="w-8 h-8 rounded-full" />
        </div>
      </div>
    ))}
  </div>
);

// Form Skeleton
export const FormSkeleton = ({ fields = 4 }) => (
  <div className="space-y-4">
    {[...Array(fields)].map((_, i) => (
      <div key={i} className="space-y-2">
        <SkeletonBase className="h-4 w-24" />
        <SkeletonBase className="h-12 w-full" />
      </div>
    ))}
    <div className="flex gap-3 pt-4">
      <SkeletonBase className="h-12 flex-1" />
      <SkeletonBase className="h-12 w-32" />
    </div>
  </div>
);

// Loading Page Skeleton (full page)
export const PageSkeleton = () => (
  <div className="space-y-6 p-6">
    {/* Header Skeleton */}
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <SkeletonBase className="h-8 w-48" />
        <SkeletonBase className="h-4 w-64" />
      </div>
      <SkeletonBase className="h-10 w-32" />
    </div>

    {/* Stats Skeleton */}
    <StatsCardSkeleton count={3} />

    {/* Content Skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-4">
        <SkeletonBase className="h-6 w-32" />
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <TableRowSkeleton key={i} columns={3} />
          ))}
        </div>
      </div>
      <div className="space-y-4">
        <SkeletonBase className="h-6 w-32" />
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <TableRowSkeleton key={i} columns={2} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Default export with common skeletons
export default {
  CardSkeleton,
  TextSkeleton,
  TitleSkeleton,
  ParagraphSkeleton,
  ImageCardSkeleton,
  TableRowSkeleton,
  GalleryGridSkeleton,
  TestimonialListSkeleton,
  FAQItemSkeleton,
  PackageCardSkeleton,
  StatsCardSkeleton,
  FormSkeleton,
  PageSkeleton,
};
